import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { toast } from "react-hot-toast";

import apiClient from "../services/apiClient";

import {
  createOrder,
} from "../services/billingService";

import {
  isBillingComplete,
} from "../utils/billingValidation";

/* ====================================================== */
/* CONFIG                                                 */
/* ====================================================== */

const POLL_INTERVAL = 2000;

const POLL_TIMEOUT = 45000;

/* ====================================================== */
/* HOOK                                                   */
/* ====================================================== */

export default function useUpgradeToPro(
  company,
  {
    onActivated,
    refreshCompany,
  } = {}
) {

  /* ==================================================== */
  /* STATE                                                */
  /* ==================================================== */

  const [open, setOpen] =
    useState(false);

  const [status, setStatus] =
    useState("idle");

  const [error, setError] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [pricing, setPricing] =
    useState(null);

  const [
    activationData,
    setActivationData,
  ] = useState(null);

  /* ==================================================== */
  /* REFS                                                 */
  /* ==================================================== */

  const pollTimerRef =
    useRef(null);

  const pollStartRef =
    useRef(null);

  const razorpayRef =
    useRef(null);

  const mountedRef =
    useRef(true);

  /* ==================================================== */
  /* DERIVED                                              */
  /* ==================================================== */

  const billingComplete =
    isBillingComplete(
      company
    );

  /* ==================================================== */
  /* HELPERS                                              */
  /* ==================================================== */

  const clearPolling =
    useCallback(() => {

      if (
        pollTimerRef.current
      ) {

        clearInterval(
          pollTimerRef.current
        );

        pollTimerRef.current =
          null;

      }

    }, []);

  const resetState =
    useCallback(() => {

      setStatus("idle");

      setError(null);

      setLoading(false);

      setPricing(null);

      setActivationData(
        null
      );

    }, []);

  /* ==================================================== */
  /* MODAL                                                */
  /* ==================================================== */

  const openModal =
    useCallback(() => {

      setError(null);

      setStatus(
        "confirming"
      );

      setOpen(true);

    }, []);

  const closeModal =
    useCallback(() => {

      if (loading) {
        return;
      }

      clearPolling();

      resetState();

      setOpen(false);

    }, [
      loading,
      clearPolling,
      resetState,
    ]);

  /* ==================================================== */
  /* COMPANY REFRESH                                      */
  /* ==================================================== */

  const fetchCompany =
    useCallback(async () => {

      try {

        if (
          typeof refreshCompany ===
          "function"
        ) {

          return await refreshCompany(
            company?._id
          );

        }

        const res =
          await apiClient.get(
            `/company/${company?._id}`
          );

        return (
          res?.data?.company ||
          res?.data ||
          null
        );

      } catch (err) {

        console.error(
          "[Upgrade] Company refresh failed:",
          err
        );

        return null;

      }

    }, [
      company?._id,
      refreshCompany,
    ]);

  /* ==================================================== */
  /* ACTIVATION POLLING                                   */
  /* ==================================================== */

  const waitForActivation =
    useCallback(
      async (
        expectedPlan =
          "pro"
      ) => {

        clearPolling();

        setStatus(
          "waiting_for_activation"
        );

        pollStartRef.current =
          Date.now();

        pollTimerRef.current =
          setInterval(
            async () => {

              try {

                const updatedCompany =
                  await fetchCompany();

                if (
                  !updatedCompany
                ) {
                  return;
                }

                const isActivated =
                  updatedCompany?.plan ===
                    expectedPlan &&
                  updatedCompany?.subscriptionStatus ===
                    "active";

                if (
                  isActivated
                ) {

                  clearPolling();

                  setActivationData(
                    updatedCompany
                  );

                  setStatus(
                    "activated"
                  );

                  setLoading(
                    false
                  );

                  toast.success(
                    `🎉 Your company is now on ${expectedPlan.toUpperCase()}!`,
                    {
                      duration: 5000,
                    }
                  );

                  if (
                    typeof onActivated ===
                    "function"
                  ) {

                    await onActivated(
                      updatedCompany
                    );

                  }

                  setTimeout(
                    () => {

                      if (
                        mountedRef.current
                      ) {

                        resetState();

                        setOpen(
                          false
                        );

                      }

                    },
                    1200
                  );

                  return;

                }

                const exceededTimeout =
                  Date.now() -
                    pollStartRef.current >
                  POLL_TIMEOUT;

                if (
                  exceededTimeout
                ) {

                  clearPolling();

                  setStatus(
                    "activation_delayed"
                  );

                  setLoading(
                    false
                  );

                  setError(
                    "Payment received, but activation is taking longer than expected. Please refresh in a moment."
                  );

                  toast.success(
                    "Payment received successfully"
                  );

                }

              } catch (err) {

                console.error(
                  "[Upgrade] Activation polling error:",
                  err
                );

              }

            },
            POLL_INTERVAL
          );

      },
      [
        clearPolling,
        fetchCompany,
        onActivated,
        resetState,
      ]
    );

  /* ==================================================== */
  /* START UPGRADE                                        */
  /* ==================================================== */

  const startUpgrade =
    useCallback(
      async ({
        promoCode,
      } = {}) => {

        try {

          if (
            loading
          ) {
            return;
          }

          if (
            !company?._id
          ) {

            setError(
              "Company context missing. Please refresh."
            );

            return;

          }

          if (
            !window.Razorpay
          ) {

            setError(
              "Razorpay SDK failed to load. Please refresh the page."
            );

            return;

          }

          setError(null);

          setLoading(true);

          /* ------------------------------------------------ */
          /* BILLING WARNING                                  */
          /* ------------------------------------------------ */

          if (
            !billingComplete
          ) {

            toast(
              "Billing profile incomplete. Payment will continue, but invoice sync may be delayed.",
              {
                duration: 5000,
                icon: "⚠️",
              }
            );

          }

          /* ------------------------------------------------ */
          /* CREATE ORDER                                     */
          /* ------------------------------------------------ */

          setStatus(
            "creating_order"
          );

          const response =
            await createOrder({
              plan: "pro",

              ...(promoCode
                ? {
                    promoCode,
                  }
                : {}),
            });

          /* ------------------------------------------------ */
          /* NORMALIZE RESPONSE                               */
          /* ------------------------------------------------ */

          const order =
            response?.order ||
            response;

          if (
            !order?.id
          ) {

            console.error(
              "[Upgrade] Invalid order:",
              response
            );

            throw new Error(
              "Invalid payment order response"
            );

          }

          setPricing(
            response?.pricing ||
              null
          );

          setStatus(
            "opening_checkout"
          );

          /* ------------------------------------------------ */
          /* CHECKOUT                                         */
          /* ------------------------------------------------ */

          const razorpay =
            new window.Razorpay(
              {
                key:
                  process.env.REACT_APP_RAZORPAY_KEY,

                order_id:
                  order.id,

                amount:
                  order.amount,

                currency:
                  order.currency ||
                  "INR",

                name:
                  company?.legalName ||
                  company?.name ||
                  "HeadwaySpot",

                description:
                  "Upgrade to Pro Plan",

                image:
                  company?.logoUrl ||
                  undefined,

                prefill: {

                  email:
                    company
                      ?.contact
                      ?.email ||
                    "",

                  contact:
                    company
                      ?.contact
                      ?.phone ||
                    "",

                },

                notes: {

                  companyId:
                    company._id,

                  plan:
                    "pro",

                },

                theme: {
                  color:
                    "#F97316",
                },

                handler:
                  async (
                    paymentResponse
                  ) => {

                    console.log(
                      "✅ Payment success:",
                      paymentResponse
                    );

                    toast.success(
                      "Payment successful"
                    );

                    setStatus(
                      "payment_success"
                    );

                    setLoading(
                      false
                    );

                    await waitForActivation(
                      "pro"
                    );

                  },

                modal: {

                  escape: true,

                  backdropclose:
                    false,

                  ondismiss:
                    () => {

                      if (
                        status ===
                        "activated"
                      ) {
                        return;
                      }

                      setLoading(
                        false
                      );

                      setStatus(
                        "cancelled"
                      );

                    },

                },

              }
            );

          razorpayRef.current =
            razorpay;

          razorpay.on(
            "payment.failed",
            (
              failureResponse
            ) => {

              console.error(
                "❌ Razorpay payment failed:",
                failureResponse
              );

              setLoading(
                false
              );

              setStatus(
                "failed"
              );

              const message =
                failureResponse
                  ?.error
                  ?.description ||
                "Payment failed. Please try again.";

              setError(
                message
              );

              toast.error(
                message
              );

            }
          );

          razorpay.open();

        } catch (err) {

          console.error(
            "[Upgrade] Flow failed:",
            err
          );

          setLoading(
            false
          );

          setStatus(
            "failed"
          );

          const message =
            err?.response
              ?.data
              ?.message ||
            err?.message ||
            "Failed to start upgrade flow";

          setError(
            message
          );

          toast.error(
            message
          );

        }

      },
      [
        loading,
        company,
        billingComplete,
        waitForActivation,
        status,
      ]
    );

  /* ==================================================== */
  /* CLEANUP                                              */
  /* ==================================================== */

  useEffect(() => {

    mountedRef.current =
      true;

    return () => {

      mountedRef.current =
        false;

      clearPolling();

      try {

        if (
          razorpayRef.current
        ) {
          razorpayRef.current.close();
        }

      } catch (err) {}

    };

  }, [clearPolling]);

  /* ==================================================== */

  return {

    /* modal */
    open,
    openModal,
    closeModal,

    /* actions */
    startUpgrade,

    /* state */
    status,
    error,
    loading,
    pricing,
    activationData,

    /* helpers */
    billingComplete,

    /* derived */
    isWaiting:
      status ===
      "waiting_for_activation",

    isActivated:
      status ===
      "activated",

    isFailed:
      status ===
      "failed",

  };

}